import React, { useState } from 'react';
import { employees, earningCodes, deductionCodes } from '../data/mockData';

function initials(ee) {
  return (ee.firstName[0] + ee.lastName[0]).toUpperCase();
}

function classLabel(code) {
  return code === 'E' ? 'W-2 Employee' : code === 'I' ? '1099 Contractor' : 'Payee';
}

function payBasisLabel(code) {
  return code === 'H' ? 'Hourly' : code === 'S' ? 'Salary' : 'Flat';
}

export default function EmployeeProfiles() {
  const [view, setView] = useState('cards');
  const [selected, setSelected] = useState(null);

  const getEarningDesc = code => earningCodes.find(e => e.code === code)?.description || code;
  const getDedDesc = code => deductionCodes.find(d => d.code === code)?.description || code;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Employee Profiles</h2>
          <p>{employees.length} employees · Fetched via Step 2 before parse</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn btn-sm ${view === 'cards' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('cards')}>Cards</button>
          <button className={`btn btn-sm ${view === 'table' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('table')}>Table</button>
        </div>
      </div>

      {view === 'cards' && (
        <div className="ee-grid">
          {employees.map(ee => {
            const isIssue = ee.employeeUniqueId === 'GUID_REQUIRED';
            const isContractor = ee.classificationCode === 'I';
            return (
              <div
                key={ee.employeeNumber}
                className={`ee-card ${isIssue ? 'has-issue' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelected(selected?.employeeNumber === ee.employeeNumber ? null : ee)}
              >
                <div className="ee-card-top">
                  <div className={`ee-avatar ${isContractor ? 'contractor' : isIssue ? 'issue' : ''}`}>
                    {initials(ee)}
                  </div>
                  <div>
                    <div className="ee-name">{ee.firstName} {ee.lastName}</div>
                    <div className="ee-num">{ee.employeeNumber}</div>
                    {isIssue && <span className="badge badge-critical" style={{ marginTop: 3 }}>GUID UNRESOLVED</span>}
                  </div>
                </div>
                <div className="ee-meta">
                  <div className="ee-meta-row">
                    <span className="label">Classification</span>
                    <span className="value">
                      <span className={`badge ${isContractor ? 'badge-high' : 'badge-primary'}`}>{classLabel(ee.classificationCode)}</span>
                    </span>
                  </div>
                  <div className="ee-meta-row">
                    <span className="label">Pay Basis</span>
                    <span className="value">{payBasisLabel(ee.payBasisCode)}</span>
                  </div>
                  <div className="ee-meta-row">
                    <span className="label">Pay Rate</span>
                    <span className="value mono" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {ee.payRate1 > 0 ? `$${ee.payRate1.toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div className="ee-meta-row">
                    <span className="label">Department</span>
                    <span className="value" style={{ fontSize: 12 }}>{ee.department}</span>
                  </div>
                  <div className="ee-meta-row">
                    <span className="label">Direct Deposit</span>
                    <span className="value">{ee.isDirectDepositEnabled ? <span style={{ color: 'var(--success)' }}>✓ Enrolled</span> : <span style={{ color: 'var(--error)' }}>✕ Paper check</span>}</span>
                  </div>
                </div>

                <div className="ee-earnings-chips">
                  {ee.activeEarnings.map(e => (
                    <span key={e} className="chip" title={getEarningDesc(e)}>{e} · {getEarningDesc(e)}</span>
                  ))}
                </div>
                {ee.activeDeductions.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {ee.activeDeductions.map(d => (
                      <span key={d} className="chip chip-ded" title={getDedDesc(d)}>{d}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === 'table' && (
        <div className="card">
          <div className="card-header">
            <h2>Employee Roster</h2>
            <span className="card-count">{employees.length} employees</span>
          </div>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>GUID</th>
                  <th>Classification</th>
                  <th>Pay Basis</th>
                  <th className="num">Pay Rate 1</th>
                  <th>Department</th>
                  <th>Direct Deposit</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(ee => {
                  const isIssue = ee.employeeUniqueId === 'GUID_REQUIRED';
                  const isContractor = ee.classificationCode === 'I';
                  return (
                    <tr key={ee.employeeNumber}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className={`ee-avatar ${isContractor ? 'contractor' : isIssue ? 'issue' : ''}`} style={{ width: 32, height: 32, fontSize: 12 }}>
                            {initials(ee)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{ee.firstName} {ee.lastName}</div>
                            <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{ee.employeeNumber}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`mono ${isIssue ? '' : ''}`} style={{ fontSize: 11, color: isIssue ? 'var(--error)' : 'var(--ink-4)' }}>
                          {isIssue ? '⚠ GUID_REQUIRED' : ee.employeeUniqueId}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${isContractor ? 'badge-high' : 'badge-primary'}`}>
                          {classLabel(ee.classificationCode)}
                        </span>
                      </td>
                      <td>{payBasisLabel(ee.payBasisCode)}</td>
                      <td className="num mono">{ee.payRate1 > 0 ? `$${ee.payRate1.toFixed(2)}` : '—'}</td>
                      <td style={{ fontSize: 12 }}>{ee.department}</td>
                      <td>
                        {ee.isDirectDepositEnabled
                          ? <span className="badge badge-success">✓ DD</span>
                          : <span className="badge badge-neutral">Paper</span>}
                      </td>
                      <td>
                        {isIssue
                          ? <span className="badge badge-critical">GUID_REQUIRED</span>
                          : <span className="badge badge-success">Active</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
